import { useAtomValue } from 'jotai'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import chatButton from '../../../assets/images/buttons/chat-button.svg'
import menuButton from '../../../assets/images/buttons/menu-button.svg'
import rotateButton from '../../../assets/images/buttons/rotate-button.svg'
import smileButton from '../../../assets/images/buttons/smile-button.svg'
import handHistoryButton from '../../../assets/images/buttons/some-button.svg'
import rankButton from '../../../assets/images/icons/ranking.svg'
import { useModal } from '../../../hooks'
import { userSettingsAtom } from '../../../store/atoms'
import type { ECurrency } from '../../../types/enums'
import { EModalName } from '../../../types/enums'
import { Icon } from '../../atoms'
import {
  Chat,
  MultiTable,
  SupportButton,
  TableMenuDrawer,
  TableMenuHandHistory,
} from '..'
import { TableMenuLeaderboard } from '../TableMenuLeaderboard'
import {
  actionIcon,
  supportIcon,
  tableTabs,
  topMenu,
  unactiveActionIcon,
} from './index.css'

export const TableMenuBar = (props: {
  tableId: string
  userId: string
  currency?: ECurrency
  playersCount: number
  isUserSit: boolean
  isPaused: boolean
  isAwaitingDeletion: boolean
  showAdminSettings: boolean
  multiTableProps: React.ComponentProps<typeof MultiTable>
  onClickDelete: () => void
  onClickLeave: () => void
}) => {
  const { openModal, closeModal } = useModal()
  const { t } = useTranslation()
  const userSettings = useAtomValue(userSettingsAtom)

  const onClickMenu = async () => {
    const { closeModal } = openModal<'leave' | 'delete'>(
      ({ close }) => (
        <TableMenuDrawer
          tableId={props.tableId}
          playersCount={props.playersCount}
          isPaused={props.isPaused}
          isAwaitingDeletion={props.isAwaitingDeletion}
          showAdminSettings={props.showAdminSettings}
          close={close}
        />
      ),
      {
        title: 'Menu',
        position: 'left',
        closeOnClickOutside: true,
        name: EModalName.TABLE_DRAWER,
      },
    )
    const result = await closeModal
    if (result === 'leave') {
      props.onClickLeave()
      return
    }
    if (result === 'delete') {
      props.onClickDelete()
      return
    }
  }

  const onClickLeaderboard = () => {
    openModal(
      () => (
        <TableMenuLeaderboard
          tableId={props.tableId}
          currency={props.currency}
        />
      ),
      {
        title: t('modal.leaderboard.header'),
        position: 'right',
        closeOnClickOutside: true,
        name: EModalName.LEADERBOARD,
      },
    )
  }

  const onClickChat = () => {
    openModal(
      () => (
        <Chat
          tableId={props.tableId}
          userId={props.userId}
          canSend={props.isUserSit}
        />
      ),
      {
        title: 'Chat',
        position: 'bottom',
        closeOnClickOutside: true,
        name: EModalName.CHAT,
      },
    )
  }

  const onClickHandHistory = () => {
    openModal<'leave' | null>(
      () => (
        <TableMenuHandHistory
          tableId={props.tableId}
          userId={props.userId}
          valuesInBigBlinds={userSettings.valuesInBigBlinds}
        />
      ),
      {
        title: t('modal.recentHands.header'),
        position: 'full',
        closeOnClickOutside: true,
        name: EModalName.HAND_HISTORY,
      },
    )
  }

  useEffect(() => {
    return () => {
      for (const modalName of [
        EModalName.CHAT,
        EModalName.HAND_HISTORY,
        EModalName.LEADERBOARD,
        EModalName.TABLE_DRAWER,
      ]) {
        closeModal({
          name: modalName,
        })
      }
    }
  }, [])

  return (
    <div className={topMenu}>
      <Icon
        src={menuButton}
        className={actionIcon}
        alt='Menu'
        analytic={{
          name: 'menu_button',
        }}
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onClick={onClickMenu}
      />

      <div className={tableTabs}>
        <MultiTable {...props.multiTableProps} />
      </div>

      <SupportButton className={supportIcon} />

      <Icon
        src={handHistoryButton}
        className={actionIcon}
        alt='Hand history'
        analytic={{
          name: 'hand_history',
        }}
        onClick={onClickHandHistory}
      />
      <Icon
        src={rankButton}
        className={actionIcon}
        alt='Leaderboard'
        analytic={{
          name: 'leaderboard',
        }}
        onClick={onClickLeaderboard}
      />
      <Icon
        src={rotateButton}
        alt='Rotate table orientation'
        analytic={{
          name: 'change_table_orientation',
        }}
        className={unactiveActionIcon}
      />
      <Icon
        src={smileButton}
        alt='Send emoji'
        analytic={{
          name: 'emoji',
        }}
        className={unactiveActionIcon}
      />
      <Icon
        src={chatButton}
        className={actionIcon}
        alt='Chat'
        analytic={{
          name: 'table_chat',
        }}
        onClick={onClickChat}
      />
    </div>
  )
}
